import { ref, onBeforeUnmount } from 'vue';
import { marked } from 'marked';
import {
  getTranscriptionApi,
  generateSummaryApi,
} from '@/api/recordingApi';

// Configure marked to render headings as bold text for rich editor compatibility
const renderer = new marked.Renderer();
renderer.heading = ({ text }) => `<p><b>${text}</b></p>\n`;
marked.use({ renderer });

const TRANSCRIPTION_POLL_INTERVAL = 5000;

/**
 * Composable for managing transcription polling, AI summary generation,
 * and prompt management.
 *
 * @param {Object} options
 * @param {import('vue').Ref<string|number>} options.sessionId - Current session ID ref
 * @param {import('vue').Ref<Array>} options.recordings - Recordings from props (fallback for session ID)
 * @param {Function} [options.onTranscriptionReady] - Called when transcription becomes available
 */
export function useTranscription({ sessionId, recordings, onTranscriptionReady }) {
  // --- State ---
  const isTranscribing = ref(false); // Editor overlay loading (first recording)
  const isSessionSwitching = ref(false); // Panel-level loading (switching sessions)
  const hasTranscription = ref(false);
  const transcriptionText = ref('');
  const summaryText = ref('');
  const isRegenerating = ref(false);
  const hasFetchedSummary = ref(false);
  const summaryRecordId = ref(null);
  const selectedPromptId = ref('1');
  const showManagePromptsModal = ref(false);

  let transcriptionPollTimer = null;

  // --- Polling ---
  const stopTranscriptionPolling = () => {
    if (transcriptionPollTimer) {
      clearTimeout(transcriptionPollTimer);
      transcriptionPollTimer = null;
    }
  };

  const getTranscription = async () => {
    if (!sessionId.value) return;

    stopTranscriptionPolling();

    // Always show loading in the transcription panel only (not full editor overlay)
    hasTranscription.value = true;
    isSessionSwitching.value = true;

    const poll = async () => {
      try {
        const response = await getTranscriptionApi(sessionId.value);
        const text = response?.full_transcription || '';

        if (!text) {
          transcriptionPollTimer = setTimeout(poll, TRANSCRIPTION_POLL_INTERVAL);
          return;
        }

        transcriptionText.value = text;
        hasTranscription.value = true;
        isTranscribing.value = false;
        isSessionSwitching.value = false;
        onTranscriptionReady?.();
      } catch (error) {
        if (error.response?.status === 204 || error.response?.status === 202) {
          transcriptionPollTimer = setTimeout(poll, TRANSCRIPTION_POLL_INTERVAL);
          return;
        }
        console.error('Transcription failed:', error);
        isTranscribing.value = false;
        isSessionSwitching.value = false;
      }
    };

    await poll();
  };

  // --- Summary ---
  const getEffectiveSessionId = () => sessionId.value || recordings?.value?.[0]?.id;

  const generateSummary = async () => {
    const sid = getEffectiveSessionId();
    if (!sid) return;

    isRegenerating.value = true;
    try {
      const response = await generateSummaryApi(sid, {
        prompt_id: selectedPromptId.value,
      });
      summaryText.value = marked(response.summary || '');
      summaryRecordId.value = response.summary_record_id;
      hasFetchedSummary.value = true;
    } catch (error) {
      console.error('Summary generation failed:', error);
    } finally {
      isRegenerating.value = false;
    }
  };

  const regenerateSummary = async () => {
    const sid = getEffectiveSessionId();
    if (!sid) return;

    isRegenerating.value = true;
    try {
      const response = await generateSummaryApi(sid, {
        prompt_id: selectedPromptId.value,
      });
      summaryText.value = marked(response.summary || '');
      summaryRecordId.value = response.summary_record_id;
    } catch (error) {
      console.error('Regeneration failed:', error);
    } finally {
      isRegenerating.value = false;
    }
  };

  const handleTabChange = (tab) => {
    if (tab === 'summary' && !hasFetchedSummary.value && !summaryText.value) {
      generateSummary();
    }
  };

  const handleApplyPrompt = async (promptId) => {
    showManagePromptsModal.value = false;
    selectedPromptId.value = promptId;
    await regenerateSummary();
  };

  /**
   * Reset transcription/summary state (e.g., when switching notes)
   */
  const resetTranscriptionState = () => {
    hasTranscription.value = false;
    transcriptionText.value = '';
    summaryText.value = '';
    hasFetchedSummary.value = false;
    summaryRecordId.value = null;
    isTranscribing.value = false;
    isSessionSwitching.value = false;
    stopTranscriptionPolling();
  };

  /**
   * Reset summary state only (e.g., when switching sessions)
   */
  const resetSummaryState = () => {
    hasFetchedSummary.value = false;
    summaryText.value = '';
  };

  // --- Cleanup ---
  onBeforeUnmount(() => {
    stopTranscriptionPolling();
  });

  return {
    // State
    isTranscribing,
    isSessionSwitching,
    hasTranscription,
    transcriptionText,
    summaryText,
    isRegenerating,
    hasFetchedSummary,
    summaryRecordId,
    selectedPromptId,
    showManagePromptsModal,

    // Methods
    getTranscription,
    generateSummary,
    regenerateSummary,
    handleTabChange,
    handleApplyPrompt,
    resetTranscriptionState,
    resetSummaryState,
    stopTranscriptionPolling,
  };
}
